/**
 * Platform Realtime — Contrato de eventos (cliente).
 *
 * Aplicación DETERMINISTA de eventos canónicos sobre el estado de React:
 *  - product:changed  → patch del producto en el estado (sin refetch)
 *  - protección STALE: si event.version < updatedAt local, se ignora
 *  - protección OUT-OF-ORDER: la misma regla de versión
 *  - create (producto nuevo) → se agrega; delete/hide (hidden) → se remueve
 *
 * El refetch completo queda SOLO como resync (reconexión/visibilitychange),
 * nunca como reacción al evento.
 */

/**
 * Aplica un evento product:changed al estado local. Devuelve el nuevo array.
 * KF25-UX-001B F1: el resultado se NORMALIZA por position (orden canónico del
 * carrusel) — un producto nuevo (position 0) aparece en su ubicación visual
 * correcta al instante, en lugar de quedar append al final del array.
 * Desempate por updatedAt DESC: las posiciones del cliente pueden estar
 * stale (los desplazamientos de insertProduct no emiten eventos — D3), y el
 * producto recién tocado es siempre el más reciente — semántica del carrusel
 * (lo nuevo al frente), coherente con el orden canónico del servidor.
 */
const byPosition = (a, b) =>
  (a.position ?? 0) - (b.position ?? 0) ||
  String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''));

export function applyProductEvent(products, evt) {
  if (!evt || evt.entityId === undefined || !evt.current) return products;
  const code = evt.entityId;
  const cur = evt.current;
  const version = evt.version || cur.updatedAt;

  const idx = products.findIndex((p) => p.code === code);
  if (idx === -1) {
    // Producto nuevo: solo se agrega si es visible (no hidden)
    if (cur.hidden) return products;
    return [...products, { ...cur }].sort(byPosition);
  }
  const existing = products[idx];
  // Protección stale/out-of-order: eventos con versión <= estado aplicado se ignoran
  if (version && existing.updatedAt && version < existing.updatedAt) return products;
  if (existing.updatedAt && version === existing.updatedAt) return products;

  if (cur.hidden) {
    // Soft delete / hide → remover del feed
    return products.filter((p) => p.code !== code);
  }
  const next = [...products];
  next[idx] = { ...existing, ...cur, updatedAt: version || existing.updatedAt };
  return next.sort(byPosition);
}

/** Filtro de visibilidad del feed (hidden se excluye localmente también). */
export function isProductVisible(p) {
  return !p.hidden && typeof p.position === 'number' && p.position >= 0;
}

/** Guard de secuencia para refetch: solo aplica la respuesta más reciente. */
export function createFetchGuard() {
  let seq = 0;
  return {
    next() {
      seq += 1;
      const mySeq = seq;
      return {
        isCurrent() { return mySeq === seq; },
        invalidate() { seq += 1; }
      };
    }
  };
}
